import { fetchAuth } from "../../../shared/api/ApiService"
import { ITutorPaymentDetail } from "../../tutor"

export interface IUserRole {
    role_id: number;
    role_name: string;
    role_code: string;
}

export interface IUserWorkTg {
    id: number;
    name: string;
}

export interface IUserPersonalData {
    user_id: number;
    full_name: string;
    email: string;
    phone: string;
    tg: string;
    created_at?: string;
    is_active: boolean;
    is_archive: boolean;
    legacy_admin_id: number;
    legacy_role_id: number;
    cost_per_hour: string;
    cost_per_shift?: string;
    cost_per_month: string;
    balance: string;
    tag_ids: number[];
    gender: string;
    roles: IUserRole[];
    work_tgs: IUserWorkTg[];
    payment_details: ITutorPaymentDetail[];
}

export interface IUserPersonalDataUpdate {
    user_id: number;
    full_name: string;
    email: string;
    phone: string;
    tg: string;
    is_active: boolean;
    cost_per_hour: string;
    cost_per_shift?: string;
    cost_per_month: string;
    desired_hours: string;
    tag_ids: number[];
    gender: string;
}

interface IUserRoleResponse {
    roleId: string;
    roleName: string;
    roleCode: string;
}

interface IUserWorkTgResponse {
    id: string;
    name: string;
}

interface IUserPaymentDetailResponse {
    id: string;
    type: ITutorPaymentDetail['type'];
    recipientName: string;
    cardNumber: string;
    phoneNumber: string;
    bankName: string;
    accountNumber: string;
    bik: string;
    inn: string;
    isPriority: boolean;
}

interface IUserPersonalDataResponse {
    userId: string;
    fullName: string;
    email: string;
    phone: string;
    tg: string;
    createdAt: string;
    isActive: boolean;
    isArchive: boolean;
    legacyAdminId: string;
    legacyRoleId: string;
    costPerHour: string;
    costPerShift?: string;
    costPerMonth?: string;
    balance?: string;
    tagIds?: string[];
    gender: string;
    roles: IUserRoleResponse[];
    workTgs: IUserWorkTgResponse[];
    paymentDetails: IUserPaymentDetailResponse[];
}

const mapRole = (role: IUserRoleResponse): IUserRole => ({
    role_id: Number(role.roleId),
    role_name: role.roleName,
    role_code: role.roleCode,
})

const mapWorkTg = (workTg: IUserWorkTgResponse): IUserWorkTg => ({
    id: Number(workTg.id),
    name: workTg.name,
})

const mapPaymentDetail = (detail: IUserPaymentDetailResponse): ITutorPaymentDetail => ({
    id: Number(detail.id),
    type: detail.type,
    recipient_name: detail.recipientName,
    card_number: detail.cardNumber,
    phone_number: detail.phoneNumber,
    bank_name: detail.bankName,
    account_number: detail.accountNumber,
    bik: detail.bik,
    inn: detail.inn,
    is_priority: detail.isPriority,
})

const mapPaymentDetailRequest = (detail: ITutorPaymentDetail) => ({
    id: detail.id,
    type: detail.type,
    recipientName: detail.recipient_name,
    cardNumber: detail.card_number,
    phoneNumber: detail.phone_number,
    bankName: detail.bank_name,
    accountNumber: detail.account_number,
    bik: detail.bik,
    inn: detail.inn,
    isPriority: detail.is_priority,
})

const mapUserPersonalData = (user: IUserPersonalDataResponse): IUserPersonalData => ({
    user_id: Number(user.userId),
    full_name: user.fullName,
    email: user.email,
    phone: user.phone,
    tg: user.tg,
    created_at: user.createdAt,
    is_active: user.isActive,
    is_archive: user.isArchive,
    legacy_admin_id: Number(user.legacyAdminId),
    legacy_role_id: Number(user.legacyRoleId),
    cost_per_hour: user.costPerHour,
    cost_per_shift: user.costPerShift || '',
    cost_per_month: user.costPerMonth || '',
    balance: user.balance || '0',
    tag_ids: (user.tagIds || []).map(Number),
    gender: user.gender,
    roles: user.roles.map(mapRole),
    work_tgs: user.workTgs.map(mapWorkTg),
    payment_details: user.paymentDetails.map(mapPaymentDetail),
})

class UserService {
    async getPersonalData(userId: number): Promise<IUserPersonalData> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL + '/api/v1/users/' + userId)
        const data = await res.json() as {user: IUserPersonalDataResponse; availableActions: string[]}

        return mapUserPersonalData(data.user)
    }

    async updatePersonalData(user: IUserPersonalDataUpdate): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL + '/api/v1/users/' + user.user_id + '/update', {
            method: 'POST',
            body: JSON.stringify({
                userId: user.user_id,
                fullName: user.full_name,
                email: user.email,
                phone: user.phone,
                tg: user.tg,
                isActive: user.is_active,
                costPerHour: user.cost_per_hour,
                costPerShift: user.cost_per_shift,
                costPerMonth: user.cost_per_month,
                tagIds: user.tag_ids,
                gender: user.gender,
            }),
        })
    }

    async getPaymentDetails(userId: number): Promise<ITutorPaymentDetail[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL + '/api/v1/users/' + userId + '/payment_details')
        const data = await res.json() as {paymentDetails: IUserPaymentDetailResponse[]}

        return data.paymentDetails.map(mapPaymentDetail)
    }

    async savePaymentDetails(userId: number, paymentDetails: ITutorPaymentDetail[]): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL + '/api/v1/users/' + userId + '/payment_details', {
            method: 'POST',
            body: JSON.stringify({
                userId,
                paymentDetails: paymentDetails.map(mapPaymentDetailRequest),
            }),
        })
    }
}

export const userService = new UserService()
